import classNames from "classnames";
import { toast, Id } from "react-toastify";
import { connect, useDispatch } from "react-redux";
import { ReactNode, RefObject, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";

import { GoCheck as GreenTick } from "react-icons/go";

import { State } from "redux/store";

import { LIGHT_GREEN } from "helpers/constants/colors";
import { validateDatasetName } from "helpers/validations";

import VLToastComponent from "views/components/VLToastComponent";
import SelectionOption from "./SelectionOption";

import {
  getExportDataArray,
  getExportProgress,
} from "redux/SingleDataset/selectors";
import {
  setShowCart,
  setShowExportModal,
  submitExportRequest,
} from "redux/SingleDataset/actions";
import formattedDateNow from "helpers/utility/formatters";
import { amplitudeTrack } from "helpers/utility/amplitude";
import { AMP_DATA_EXPORTED } from "helpers/constants/amplitudeEvents";
import {
  AMP_EXPORT_FILE_CONTEXT,
  AMP_EXPORT_FILE_FORMAT,
  AMP_EXPORT_INCLUDES_RAW_DATA,
} from "helpers/constants/amplitudeProperties";

import XIcon from "assets/icons/XIcon";
import TodoIcon from "assets/img/colored-to-do-list.svg";
import SettingsIcon from "assets/img/colored-setting.svg";
import FolderIcon from "assets/img/colored-file-storage.svg";
import DangerCircleIcon from "assets/icons/DangerCircleIcon";

import styles from "./style.module.scss";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";
import { useLabelPropagationExport } from "hooks/LabelPropagation/useLabelPropagationExport";
import VLTooltip from "views/uikit/VLTooltip";

export type FilesToExport =
  | "SELECTED"
  | "FILTERED"
  | "DATASET"
  | "LABEL_PROPAGATION";
export type ExportFormatOptions = "JSON";

const showToast = (Element: ReactNode): Id => {
  return toast(Element, {
    autoClose: false,
    closeButton: false,
    closeOnClick: false,
  });
};

interface ExportModalProps {
  callerButtonRef: RefObject<HTMLDivElement>;
  openedFrom: "CART" | "CONTAINER_HEADER";
  exportProgress: number;
  showModal: boolean;
  exportData: any[];
}

const ExportModal = ({
  showModal,
  exportData,
  openedFrom,
  exportProgress,
  callerButtonRef,
}: ExportModalProps) => {
  const initialSelectedSource: FilesToExport =
    openedFrom === "CONTAINER_HEADER" ? "DATASET" : "SELECTED";

  const selectedExportType = "JSON";

  const [errorFlag, setErrorFlag] = useState<string>("");
  const [fileName, setFileName] = useState<string>(
    "VL-export-" + formattedDateNow(),
  );
  const [selectedItemsCount, setSelectedItemsCount] = useState<number>(0);
  const [selectedSource, setSelectedSource] = useState<FilesToExport>(
    initialSelectedSource,
  );
  const [includeRawData, setIncludeRawData] = useState<boolean>(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const toastIdRef = useRef<any>(null);
  const dispatch = useDispatch();
  const queryParamsFilters = useAllQueryParamsFilters();
  const { datasetId } = useParams<{ datasetId: string }>();

  // Get flywheel status and annotations for Label Propagation Results option
  const { isExportLabelPropagation, exportLabelPropagation } =
    useLabelPropagationExport(datasetId);

  const isExportDisabled =
    !(fileName && selectedSource) ||
    errorFlag ||
    (selectedSource === "LABEL_PROPAGATION" && !isExportLabelPropagation);

  const defaultSource: FilesToExport =
    openedFrom === "CONTAINER_HEADER" ? "DATASET" : "FILTERED";

  const inputClassNames = classNames(styles.input, {
    [styles.errorInput]: errorFlag,
    [styles.showInputIcon]: fileName !== "",
    [styles.successBorder]: !!fileName && !errorFlag,
  });

  const toggleContainerClassnames = classNames(styles.toggleContainer, {
    [styles.selectedToggleContainer]: includeRawData,
  });

  const exportButtonClassnames = classNames(styles.exportButton, {
    [styles.disabledExortButton]: isExportDisabled,
  });

  const resetStates = () => {
    setErrorFlag("");
    setFileName("VL-export-" + formattedDateNow());
    setSelectedSource(selectedItemsCount > 0 ? "SELECTED" : defaultSource);
  };

  const handleInputValidation = (value: any) => {
    let result = validateDatasetName(value);
    result ? setErrorFlag(result) : setErrorFlag("");
  };

  const handleInputChange = (e: any) => {
    let inputValue = e.target.value;
    if (inputValue === "") {
      resetStates();
    }
    setFileName(inputValue);
    handleInputValidation(inputValue);
  };

  const handleCloseButtonClick = () => {
    resetStates();
    dispatch(setShowExportModal(null));
  };

  const handleOutsideClick = (event: any) => {
    if (
      callerButtonRef.current &&
      callerButtonRef.current.contains(event.target)
    ) {
      return;
    }

    if (modalRef.current && !modalRef.current.contains(event.target)) {
      handleCloseButtonClick();
    }
  };

  const successNotification = (downloadURI: string) => {
    toastIdRef.current = showToast(
      <VLToastComponent
        status="SUCCESS"
        title={"The file is now ready!"}
        onClose={() => toast.dismiss(toastIdRef.current)}
        helperText={
          <span>
            You can download the file again by clicking this{" "}
            <a
              className={styles.toastLink}
              href={downloadURI}
              download={`${fileName}.zip`}
            >
              link
            </a>
            .
          </span>
        }
        description={
          "Your export is almost ready! You can find your ZIP file in the 'Downloads' folder shortly."
        }
      />,
    );
  };

  const errorNotification = (errorDescription: string) => {
    toastIdRef.current = showToast(
      <VLToastComponent
        status="ERROR"
        description={errorDescription}
        title={"Oops! We could not process your file"}
        onClose={() => toast.dismiss(toastIdRef.current)}
      />,
    );
  };

  const pendingNotification = () => {
    toastIdRef.current = showToast(
      <VLToastComponent
        status="IN_PROGRESS"
        progress={exportProgress}
        title={"Exporting File..."}
        onClose={() => toast.dismiss(toastIdRef.current)}
        helperText={
          "This process will continue in the background. You can close this window"
        }
        description={
          "Your export is almost ready! You can find your ZIP file in the 'Downloads' folder shortly."
        }
      />,
    );
  };

  const handleExportClick = async () => {
    if (isExportDisabled) {
      setErrorFlag("Please insert a correct file name.");
      return;
    }

    try {
      dispatch(setShowExportModal(null));
      dispatch(setShowCart(false));

      // Handle Label Propagation export differently (no progress tracking needed)
      if (selectedSource === "LABEL_PROPAGATION") {
        await exportLabelPropagation(fileName);
        toastIdRef.current = showToast(
          <VLToastComponent
            status="SUCCESS"
            title={"The file is now ready!"}
            onClose={() => toast.dismiss(toastIdRef.current)}
            description="Your export is almost ready! You can find your JSON file in the 'Downloads' folder."
          />,
        );
      } else {
        // Handle regular exports with progress tracking
        pendingNotification();
        const downloadURI = await dispatch(
          submitExportRequest(
            fileName,
            selectedExportType,
            selectedSource,
            queryParamsFilters,
            includeRawData,
          ) as any,
        );
        toast.dismiss(toastIdRef.current);
        amplitudeTrack(AMP_DATA_EXPORTED, {
          [AMP_EXPORT_INCLUDES_RAW_DATA]: includeRawData,
          [AMP_EXPORT_FILE_CONTEXT]: selectedSource,
          [AMP_EXPORT_FILE_FORMAT]: selectedExportType,
        });
        successNotification(downloadURI);
      }
    } catch (error: any) {
      toast.dismiss(toastIdRef.current);
      errorNotification(error.message);
    } finally {
      resetStates();
    }
  };

  const renderInput = () => {
    return (
      <div className={styles.inputContainer}>
        <input
          type="text"
          value={fileName}
          className={inputClassNames}
          onChange={handleInputChange}
          data-testid="export-filename-input"
          placeholder="Insert a file name e.g My Images"
        />
        <div
          className={classNames(
            fileName === "" && !errorFlag
              ? styles.disabledGreenTick
              : undefined,
          )}
        >
          {errorFlag ? (
            <VLTooltip title={errorFlag}>
              <div
                className={styles.errorIcon}
                data-testid="export-filename-error-icon"
              >
                <DangerCircleIcon variant="outline" />
              </div>
            </VLTooltip>
          ) : (
            <GreenTick
              size="1em"
              color={LIGHT_GREEN}
              className={styles.greenTick}
            />
          )}
        </div>
      </div>
    );
  };

  const renderSelectionOptions = () => {
    return (
      <div className={styles.selectionOptionsContainer}>
        {selectedItemsCount > 0 && (
          <SelectionOption
            icon={TodoIcon}
            badgeText={`${selectedItemsCount}  items`}
            title="Current Selection"
            description="Export the selected items"
            selected={selectedSource === "SELECTED"}
            onSelect={() => setSelectedSource("SELECTED")}
          />
        )}

        <SelectionOption
          icon={SettingsIcon}
          title="Matching the applied filters"
          selected={selectedSource === "FILTERED"}
          onSelect={() => setSelectedSource("FILTERED")}
          description="Export items that match the current filter selection"
        />

        <SelectionOption
          icon={FolderIcon}
          title="Entire Dataset"
          selected={selectedSource === "DATASET"}
          onSelect={() => setSelectedSource("DATASET")}
          description="Export all items from the complete dataset"
        />

        {isExportLabelPropagation && (
          <SelectionOption
            icon={FolderIcon}
            title="Label Propagation Results"
            selected={selectedSource === "LABEL_PROPAGATION"}
            onSelect={() => setSelectedSource("LABEL_PROPAGATION")}
            description="Export all Labels"
          />
        )}
      </div>
    );
  };

  useEffect(() => {
    setSelectedItemsCount(
      exportData.reduce((total, item) => {
        const excludeMediaIDs = item.excludeMediaIDs || [];
        if (item.type === "IMAGES" && item.n_images !== undefined) {
          return total + (item.n_images - excludeMediaIDs.length);
        } else if (item.type === "IMAGE" || item.type === "OBJECT") {
          return total + 1;
        } else if (item.type === "OBJECTS" && item.n_objects !== undefined) {
          return total + (item.n_objects - excludeMediaIDs.length);
        }
        return total;
      }, 0),
    );
  }, [exportData]);

  useEffect(() => {
    if (selectedItemsCount === 0) {
      setSelectedSource(defaultSource);
    } else {
      setSelectedSource("SELECTED");
    }
  }, [selectedItemsCount, defaultSource]);

  useEffect(() => {
    if (showModal) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
    //eslint-disable-next-line
  }, [showModal]);

  return showModal ? (
    <div
      ref={modalRef}
      className={styles.modalContainer}
      onClick={(e: any) => e.stopPropagation()}
    >
      <div className={styles.modalHeader}>
        <span className={styles.modalHeaderTitle}>Export data</span>
        <div
          className={styles.modalCloseBtn}
          onClick={handleCloseButtonClick}
          data-testid="export-modal-close-button"
        >
          <XIcon variant="new" />
        </div>
      </div>
      <div className={styles.divider}></div>

      <div className={styles.label}>
        Please provide a name for the file you're exporting
      </div>
      {renderInput()}

      <div className={styles.label}>
        Choose the items you want to include in your export
      </div>
      {renderSelectionOptions()}

      <div className={styles.rawImageOption}>
        <div className={styles.label}>
          Include image files (up to 5,000 images)
        </div>
        <div
          className={toggleContainerClassnames}
          onClick={() => setIncludeRawData(!includeRawData)}
        >
          <div className={styles.toggleIcon}></div>
        </div>
      </div>

      <div className={styles.divider}></div>

      <div
        className={exportButtonClassnames}
        onClick={handleExportClick}
        data-testid="export-file-button"
      >
        Export {selectedExportType} File
      </div>
    </div>
  ) : (
    <noscript />
  );
};

const mapStateToProps = (state: State) => {
  return {
    exportData: getExportDataArray(state),
    exportProgress: getExportProgress(state),
  };
};

export default connect(mapStateToProps)(ExportModal);
